import {
  Anchor,
  Button,
  Checkbox,
  Group,
  Modal,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { IconFolderOff, IconRestore, IconSearch, IconTrash } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { api } from "../../api/client";
import { useCompanyOptions, useDeleteDocuments, useModule, useNames } from "../../api/hooks";
import type { DocumentSummary } from "../../api/types";
import { useAuth } from "../../auth/auth";
import { EmptyState } from "../../components/empty-state";
import { formatBytes, formatDate } from "../../lib/format";
import { StatusBadge } from "../upload/upload-page";

interface Row extends DocumentSummary {
  originalName: string;
  sizeBytes: number;
  version: number;
  sourceKind: string;
  createdAt: string;
}

const SORTS = ["created_desc", "date_desc", "date_asc", "title"] as const;
const STATUSES = [
  "ready",
  "failed",
  "superseded",
  "queued",
  "extracting",
  "embedding",
  "deleted",
] as const;

/** Browse the archive without a search query: filters by company, type, date, status. */
export function DocumentsPage() {
  const { t } = useTranslation();
  const { lookups, company, docType } = useNames();
  const { user } = useAuth();
  const deleteOn = useModule("delete");
  const isAdmin = user?.role === "admin" && deleteOn;
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useDeleteDocuments("delete");
  const restore = useDeleteDocuments("restore");
  const q = params.get("q") ?? "";
  const [text, setText] = useState(q);
  // Back/forward and nav links change ?q= without going through the input: keep it in sync.
  useEffect(() => setText(q), [q]);
  // A selection belongs to the list it was made in: another filter, page or the bin shows other
  // rows, and "restore selected" must not carry ids of live documents over.
  const listKey = params.toString();
  useEffect(() => setSelected(new Set()), [listKey]);
  const filters = useMemo(
    () => ({
      q,
      company: params.get("company") ?? "",
      type: params.get("type") ?? "",
      status: params.get("status") ?? "",
      from: params.get("from") ?? "",
      to: params.get("to") ?? "",
      sort: (SORTS as readonly string[]).includes(params.get("sort") ?? "")
        ? (params.get("sort") as (typeof SORTS)[number])
        : "created_desc",
      page: Math.max(1, Number(params.get("page") ?? "1") || 1),
    }),
    [params, q],
  );
  const companyOptions = useCompanyOptions(filters.company);
  const set = (patch: Partial<typeof filters>) => {
    const next = { ...filters, ...patch, page: patch.page ?? 1 };
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) {
      if (v === "" || v === null || v === undefined) continue;
      if (k === "page" && v === 1) continue;
      if (k === "sort" && v === "created_desc") continue;
      p.set(k, String(v));
    }
    setParams(p);
  };
  const list = useQuery({
    queryKey: ["documents", "catalog", filters],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      api<{ items: Row[]; total: number; page: number; pageSize: number }>("/api/documents", {
        signal,
        query: {
          q: filters.q || undefined,
          company: filters.company || undefined,
          type: filters.type || undefined,
          status: filters.status || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
          sort: filters.sort,
          page: filters.page,
          pageSize: 25,
        },
      }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    set({ q: text.trim() });
  };
  const items = list.data?.items ?? [];
  const inBin = filters.status === "deleted";
  const pageIds = items.map((d) => d.id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggle = (id: number, on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const bulk = async (action: "delete" | "restore") => {
    const ids = [...selected];
    try {
      await (action === "delete" ? remove : restore).mutateAsync(ids);
      notifications.show({
        message: t(action === "delete" ? "catalog.deletedCount" : "catalog.restoredCount", {
          count: ids.length,
        }),
        color: "green",
      });
      setSelected(new Set());
      setConfirmDelete(false);
    } catch (e) {
      notifications.show({ message: (e as Error).message, color: "red" });
    }
  };

  return (
    <Stack gap="md">
      <Title order={2}>{t("catalog.title")}</Title>
      <Paper withBorder p="sm">
        <form onSubmit={submit}>
          <Group align="flex-end" gap="sm" wrap="wrap">
            <TextInput
              label={t("catalog.query")}
              value={text}
              onChange={(e) => {
                setText(e.currentTarget.value);
                // Clearing the box resets the filter without waiting for Enter.
                if (e.currentTarget.value === "" && filters.q) set({ q: "" });
              }}
              leftSection={<IconSearch size={16} />}
              placeholder={t("catalog.queryHint")}
              style={{ minWidth: 220, flex: 1 }}
            />
            <Button type="submit" variant="default" loading={list.isFetching && text !== filters.q}>
              {t("search.button")}
            </Button>
            <Select
              label={t("document.company")}
              data={companyOptions}
              value={filters.company || null}
              onChange={(v) => set({ company: v ?? "" })}
              clearable
              w={180}
            />
            <Select
              label={t("document.type")}
              data={(lookups.data?.docTypes ?? []).map((d) => ({
                value: String(d.id),
                label: docType(d.id),
              }))}
              value={filters.type || null}
              onChange={(v) => set({ type: v ?? "" })}
              clearable
              w={160}
            />
            <DateInput
              label={t("search.filters.from")}
              value={filters.from || null}
              onChange={(v) => set({ from: v ?? "" })}
              valueFormat="DD.MM.YYYY"
              clearable
              w={140}
            />
            <DateInput
              label={t("search.filters.to")}
              value={filters.to || null}
              onChange={(v) => set({ to: v ?? "" })}
              valueFormat="DD.MM.YYYY"
              clearable
              w={140}
            />
            <Select
              label={t("upload.state")}
              data={STATUSES.filter((s) => s !== "deleted" || isAdmin).map((s) => ({
                value: s,
                label: t(`status.${s}`),
              }))}
              value={filters.status || null}
              onChange={(v) => set({ status: v ?? "" })}
              clearable
              w={160}
            />
            <Select
              label={t("catalog.sort")}
              data={SORTS.map((s) => ({ value: s, label: t(`catalog.sorts.${s}`) }))}
              value={filters.sort}
              onChange={(v) => v && set({ sort: v as (typeof SORTS)[number] })}
              allowDeselect={false}
              w={200}
            />
          </Group>
        </form>
      </Paper>
      <Group justify="space-between" align="center">
        <Text size="sm" c="dimmed">
          {t("catalog.total", { count: list.data?.total ?? 0 })}
        </Text>
        {isAdmin && selected.size > 0 && (
          <Group gap="xs">
            <Text size="sm">{t("catalog.selected", { count: selected.size })}</Text>
            {inBin ? (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRestore size={14} />}
                loading={restore.isPending}
                onClick={() => bulk("restore")}
              >
                {t("catalog.restoreSelected")}
              </Button>
            ) : (
              <Button
                size="xs"
                color="red"
                variant="light"
                leftSection={<IconTrash size={14} />}
                onClick={() => setConfirmDelete(true)}
              >
                {t("catalog.deleteSelected")}
              </Button>
            )}
          </Group>
        )}
      </Group>
      <Modal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t("catalog.deleteSelected")}
      >
        <Stack>
          <Text size="sm">{t("catalog.deleteConfirm", { count: selected.size })}</Text>
          <Text size="xs" c="dimmed">
            {t("document.deleteHint")}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(false)}>
              {t("common.cancel")}
            </Button>
            <Button color="red" loading={remove.isPending} onClick={() => bulk("delete")}>
              {t("common.delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Table.ScrollContainer minWidth={760}>
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {isAdmin && (
                <Table.Th w={36}>
                  <Checkbox
                    size="xs"
                    checked={allSelected}
                    indeterminate={!allSelected && pageIds.some((id) => selected.has(id))}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      setSelected((s) => {
                        const next = new Set(s);
                        for (const id of pageIds) on ? next.add(id) : next.delete(id);
                        return next;
                      });
                    }}
                  />
                </Table.Th>
              )}
              <Table.Th>{t("document.title")}</Table.Th>
              <Table.Th>{t("document.regNumber")}</Table.Th>
              <Table.Th>{t("document.date")}</Table.Th>
              <Table.Th>{t("document.company")}</Table.Th>
              <Table.Th>{t("document.type")}</Table.Th>
              <Table.Th ta="right">{t("document.pagesLabel")}</Table.Th>
              <Table.Th miw={130}>{t("upload.state")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((d) => (
              <Table.Tr key={d.id}>
                {isAdmin && (
                  <Table.Td>
                    <Checkbox
                      size="xs"
                      checked={selected.has(d.id)}
                      onChange={(e) => toggle(d.id, e.currentTarget.checked)}
                    />
                  </Table.Td>
                )}
                <Table.Td>
                  <Anchor component={Link} to={`/documents/${d.id}`} size="sm">
                    {d.title}
                  </Anchor>
                  <Text size="xs" c="dimmed">
                    {d.originalName} · {formatBytes(d.sizeBytes)}
                    {d.version > 1 ? ` · v${d.version}` : ""}
                  </Text>
                </Table.Td>
                <Table.Td>{d.regNumber ?? ""}</Table.Td>
                <Table.Td>{formatDate(d.docDate)}</Table.Td>
                <Table.Td>{company(d.companyId)}</Table.Td>
                <Table.Td>{docType(d.docTypeId)}</Table.Td>
                <Table.Td ta="right">{d.pageCount}</Table.Td>
                <Table.Td>
                  <StatusBadge status={d.status} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {list.data && list.data.total === 0 && (
        <EmptyState
          icon={<IconFolderOff size={22} />}
          title={inBin ? t("catalog.binEmpty") : t("catalog.empty")}
          hint={
            filters.q || filters.company || filters.type || filters.from || filters.to
              ? t("catalog.emptyFiltersHint")
              : inBin
                ? undefined
                : t("catalog.emptyHint")
          }
          action={
            filters.q || filters.company || filters.type || filters.from || filters.to ? (
              <Button
                variant="light"
                size="xs"
                onClick={() => {
                  setText("");
                  set({ q: "", company: "", type: "", from: "", to: "" });
                }}
              >
                {t("search.filters.clear")}
              </Button>
            ) : undefined
          }
        />
      )}
      {list.data && list.data.total > list.data.pageSize && (
        <Pagination
          total={Math.ceil(list.data.total / list.data.pageSize)}
          value={filters.page}
          onChange={(p) => set({ page: p })}
        />
      )}
    </Stack>
  );
}
